import React, { useState, useEffect } from 'react';
import {
  Mail,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  RotateCw,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import lettersApi, { LetterSend } from '@/services/lettersApi';
import { MailDeliveryTracker } from './MailDeliveryTracker';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SendStatus = LetterSend['status'];

interface LetterSendHistoryProps {
  memberId?: number;
  programId?: number;
  enrollmentId?: number;
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<SendStatus, { label: string; color: string; icon: React.ReactNode }> = {
  created: {
    label: 'Created',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    icon: <Clock className="h-3 w-3" />,
  },
  processing: {
    label: 'Processing',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  mailed: {
    label: 'Mailed',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    icon: <Truck className="h-3 w-3" />,
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: <CheckCircle className="h-3 w-3" />,
  },
  returned: {
    label: 'Returned',
    color: 'bg-orange-100 text-orange-700 border-orange-300',
    icon: <AlertCircle className="h-3 w-3" />,
  },
  failed: {
    label: 'Failed',
    color: 'bg-red-100 text-red-700 border-red-300',
    icon: <XCircle className="h-3 w-3" />,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: SendStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.created;
  return (
    <Badge variant="outline" className={`gap-1 ${cfg.color}`}>
      {cfg.icon}
      {cfg.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LetterSendHistory: React.FC<LetterSendHistoryProps> = ({
  memberId,
  programId,
  enrollmentId,
}) => {
  const [sends, setSends] = useState<LetterSend[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [selectedSendId, setSelectedSendId] = useState<number | null>(null);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const fetchSends = async () => {
    try {
      setLoading(true);
      const filters: { member_id?: number; status?: string; program_id?: number; enrollment_id?: number } = {};
      if (memberId) filters.member_id = memberId;
      if (programId) filters.program_id = programId;
      if (enrollmentId) filters.enrollment_id = enrollmentId;
      if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;

      const data = await lettersApi.listSends(filters);
      setSends(data);
    } catch (err) {
      console.error('Failed to load letter sends:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSends();
  }, [memberId, programId, enrollmentId, statusFilter]);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleRefreshStatus = async (e: React.MouseEvent, sendId: number) => {
    e.stopPropagation();
    try {
      setRefreshingId(sendId);
      const updated = await lettersApi.refreshSendStatus(sendId);
      setSends((prev) => prev.map((s) => (s.id === sendId ? updated : s)));
    } catch (err) {
      console.error('Failed to refresh send status:', err);
    } finally {
      setRefreshingId(null);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" />
            Letter Send History
          </CardTitle>

          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="mailed">Mailed</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={fetchSends} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4" />
              )}
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading && sends.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading sends...
            </div>
          ) : sends.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Package className="mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm">No letter sends found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Mail Type</TableHead>
                    <TableHead>Sent Date</TableHead>
                    <TableHead>Delivered Date</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead className="w-[80px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sends.map((send) => (
                    <TableRow
                      key={send.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedSendId(send.id)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          <div>
                            <div>{send.recipient_name || '-'}</div>
                            {send.recipient_city && send.recipient_state && (
                              <div className="text-xs text-muted-foreground">
                                {send.recipient_city}, {send.recipient_state}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{send.template_name || '-'}</TableCell>
                      <TableCell>
                        <StatusBadge status={send.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {send.mail_type?.replace('usps_', '').replace('_', ' ') || '-'}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(send.sent_at)}</TableCell>
                      <TableCell className="text-sm">{formatDate(send.delivered_at)}</TableCell>
                      <TableCell>
                        {send.tracking_url ? (
                          <a
                            href={send.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          >
                            Track
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={refreshingId === send.id}
                          onClick={(e) => handleRefreshStatus(e, send.id)}
                          title="Refresh status"
                        >
                          {refreshingId === send.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RotateCw className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail tracker dialog */}
      {selectedSendId !== null && (
        <MailDeliveryTracker
          sendId={selectedSendId}
          onClose={() => setSelectedSendId(null)}
        />
      )}
    </>
  );
};

export default LetterSendHistory;
